/**
 * Custom smooth scrolling utility with easing animation
 * Provides start-slow, accelerate, end-slow animation curve
 */

// Easing function: ease-in-out cubic for smooth acceleration/deceleration
const easeInOutCubic = (t) => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
};

/**
 * Smoothly scrolls an element into view with custom easing
 * @param {HTMLElement} element - The element to scroll to
 * @param {number} duration - Animation duration in milliseconds (default: 800ms)
 * @param {string} position - Scroll position: 'start', 'center', 'end' (default: 'end')
 */
export const smoothScrollToElement = (element, duration = 800, position = 'end') => {
  if (!element) return;

  const startPosition = window.pageYOffset;
  const elementRect = element.getBoundingClientRect();
  const elementTop = elementRect.top + startPosition;
  
  // Calculate target position based on desired alignment
  let targetPosition;
  switch (position) {
    case 'start':
      targetPosition = elementTop;
      break;
    case 'center':
      targetPosition = elementTop - (window.innerHeight / 2) + (elementRect.height / 2);
      break;
    case 'end':
    default:
      targetPosition = elementTop - window.innerHeight + elementRect.height + 20; // 20px padding from bottom
      break;
  }

  // Ensure we don't scroll past the document bounds
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  targetPosition = Math.min(Math.max(0, targetPosition), maxScroll);

  const distance = targetPosition - startPosition;
  const startTime = performance.now();

  const animateScroll = (currentTime) => {
    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    
    // Apply easing function
    const easedProgress = easeInOutCubic(progress);
    const currentPosition = startPosition + (distance * easedProgress);
    
    window.scrollTo(0, currentPosition);

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  };

  requestAnimationFrame(animateScroll);
};

/**
 * Smoothly scrolls to the bottom of the page
 * @param {number} duration - Animation duration in milliseconds (default: 800ms)
 */
export const smoothScrollToBottom = (duration = 800) => {
  const startPosition = window.pageYOffset;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const distance = maxScroll - startPosition;
  const startTime = performance.now();

  const animateScroll = (currentTime) => {
    const elapsedTime = currentTime - startTime;
    const progress = Math.min(elapsedTime / duration, 1);
    
    const easedProgress = easeInOutCubic(progress);
    const currentPosition = startPosition + (distance * easedProgress);
    
    window.scrollTo(0, currentPosition);

    if (progress < 1) {
      requestAnimationFrame(animateScroll);
    }
  };

  requestAnimationFrame(animateScroll);
};